import { FC } from 'react';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from '@mui/icons-material';
import { Box, InputAdornment } from '@mui/material';

import FileUploadErrors from '@components/FormFileUploadErrors/FormFileUploadErrors';
import FileUploadField from '@components/FormFileUploadField/FileUploadField';
import FormTextField from '@components/FormTextField/FormTextField';
import { fileUploadErrorsSelector } from '@store/features/fileUploadState/selectors';
import removeWhiteSpaces from '@utils/removeWhiteSpaces';

import { IUserEnteredMedicationFormProps } from './UserEnteredMedicationForm.interface';

import './style.scss';

const uploadImagesSliderResponsiveSettings = [
  {
    breakpoint: 370,
    settings: {
      slidesToShow: 1,
    },
  },
  {
    breakpoint: 480,
    settings: {
      slidesToShow: 2,
    },
  },
  {
    breakpoint: 1080,
    settings: {
      slidesToShow: 3,
    },
  },
];

const UserEnteredMedicationForm: FC<IUserEnteredMedicationFormProps> = ({
  disabled,
  handleSubmit,
}) => {
  const { t } = useTranslation();
  const fileUploadErrors = useSelector(fileUploadErrorsSelector);

  return (
    <form
      className="user-entered-medication-form"
      id="user-entered-medication-form"
      noValidate
      onSubmit={handleSubmit}
    >
      <p className="user-entered-medication-form__required small">{t('common.required')}</p>

      <Field
        component={FormTextField}
        className="user-entered-medication-form__input"
        label={t('addMedicationModal.medicationName')}
        name="name"
        disabled={disabled}
        maxRows={2}
        format={removeWhiteSpaces}
        formatOnBlur
        multiline
        required
      />

      <Field
        component={FormTextField}
        className="user-entered-medication-form__input user-entered-medication-form__link"
        label={t('addMedicationModal.linkToProductPage')}
        name="productLink"
        disabled={disabled}
        InputProps={{
          startAdornment: (
            <InputAdornment className="user-entered-medication-form__input-icon" position="start">
              <Link color="inherit" />
            </InputAdornment>
          ),
        }}
        format={removeWhiteSpaces}
        formatOnBlur
      />

      {!disabled && (
        <>
          <Field
            name="images"
            component={FileUploadField}
            responsiveImagesSettings={uploadImagesSliderResponsiveSettings}
            className="user-entered-medication-form__input"
            label={t('addSupplementModal.linkToProductPage')}
            disabled={disabled}
            InputProps={{
              startAdornment: (
                <InputAdornment
                  className="user-entered-medication-form__input-icon"
                  position="start"
                >
                  <Link color="inherit" />
                </InputAdornment>
              ),
            }}
            formatOnBlur
          />
          {!!fileUploadErrors.length && (
            <Box className="user-entered-medication-form__file-errors">
              <FileUploadErrors />
            </Box>
          )}
        </>
      )}
    </form>
  );
};

export default UserEnteredMedicationForm;
